import { normalizeWidgetTheme, APIURL, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, updateSelectedSymbolInDropdown, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';


export class TraderverseStockAssetSpecificUpgradesDowngrades {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private currentChart: any;
  private widgetResizeObserver: ResizeObserver | null = null;
  private lastFetchedAt: Date | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.injectChartLibrary();
    this.attachStyles();
    this.setupEventListeners();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    const symbolOptions = this.fetchSymbols()
      .map(s => `<option value="${s.value}">${s.label}</option>`).join('');
let themeClass = 'defaultDarkMode';
let widgetInstanceCounter = 0;
const widgetIndex = widgetInstanceCounter++;
let dataTheme = 'default-dark';

if (this.config.theme === 'white') {
  themeClass = 'whiteMode';
  dataTheme = 'white';
} else if (this.config.theme === 'black') {
  themeClass = 'blackMode';
  dataTheme = 'black';
} else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
          <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}"   data-testing-id="asset-upgrades-downgrades-widget-${widgetIndex}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Asset Specific Upgrades and Downgrades</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<tooltip-wrapper id="lastUpdatedTooltip"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.625 6.625C0.625 7.41293 0.780195 8.19315 1.08172 8.9211C1.38325 9.64905 1.82521 10.3105 2.38236 10.8676C2.93951 11.4248 3.60095 11.8667 4.3289 12.1683C5.05685 12.4698 5.83707 12.625 6.625 12.625C7.41293 12.625 8.19315 12.4698 8.9211 12.1683C9.64905 11.8667 10.3105 11.4248 10.8676 10.8676C11.4248 10.3105 11.8667 9.64905 12.1683 8.9211C12.4698 8.19315 12.625 7.41293 12.625 6.625C12.625 5.0337 11.9929 3.50758 10.8676 2.38236C9.74242 1.25714 8.2163 0.625 6.625 0.625C5.0337 0.625 3.50758 1.25714 2.38236 2.38236C1.25714 3.50758 0.625 5.0337 0.625 6.625Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> <path d="M6.625 3.2915V6.62484L8.625 8.62484" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/> </svg><tooltip><span>Last Updated</span><strong>N/A</strong></tooltip></tooltip-wrapper><i class="pi pi-ellipsis-v"></i></div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                              <div class="trader-sm-card" id="selected-symbol">
                              ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: "stock", name: "" })}
                              </div>
                              <span id="symbol-dropdown-placeholder"></span>
                              </div>`}
              <span></span>
              <div class="item">
                
              </div>
            </div>

            <div class="chartDv" id="analystContainer">
           
            </div>

          </div>
        </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }
  onSelectChange(stock: any) {
    this.changeSymbol(stock.symbol, stock);
  }
  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    dropdown?.addEventListener('change', e => {
      const value = (e.target as HTMLSelectElement).value;
      this.changeSymbol(value);
    });
    if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);
      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async setupChart(symbol: string): Promise<void> {
    try {
      const [analystRes] = await Promise.all([
        apiGet({
          method: "POST",
          url: `${APIURL}/v1/overview/fmp_analyst_rating`,
          body: { symbol },
        }),
      ]);
      this.currentData = {};
      if (analystRes?.ok) {
        const data = await analystRes.json();
        const first = (arr: any) =>
          Array.isArray(arr) ? arr[0] : arr && typeof arr === 'object' && !Array.isArray(arr) ? arr : undefined;
        this.currentData.analystRating = first(data?.data);
      }
      const fromApi = (this.currentData as any)?.last_updated_at ?? (this.currentData?.analystRating as any)?.last_updated_at;
      this.lastFetchedAt = fromApi ? new Date(fromApi) : new Date();
      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    }
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }

  private updateLastUpdatedTooltip(): void {
    const lastUpdated = this.lastFetchedAt ? this.formatLastUpdated(this.lastFetchedAt) : 'N/A';
    const tooltip = this.shadowRoot.querySelector('#lastUpdatedTooltip tooltip strong');
    if (tooltip) tooltip.innerHTML = lastUpdated.replace(' ', '<br>');
  }

  private updateDisplay(): void {
    this.updateLastUpdatedTooltip();
    const container = this.shadowRoot.querySelector('#analystContainer') as HTMLElement;
    if (!container) return;
    if (this.currentData == null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    if (!this.currentData?.analystRating) {
      container.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      return;
    }
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderAnalystRating();
  }

  private renderAnalystRating(): void {
    const container = this.shadowRoot.querySelector('#analystContainer');
    if (!container) return;
    if (this.currentData?.analystRating) {
  container.setAttribute('data-testing-id', 'analyst-upgrades-downgrades-chart');
} else {
  container.removeAttribute('data-testing-id');
}

    if (!this.currentData.analystRating) {
      container.innerHTML = `<div class="noData"><p>No Data Available</p></div>`;
      return;
    }

    const totalAnalysts =
      (this.currentData.analystRating.strong_buy || 0) +
      (this.currentData.analystRating.buy || 0) +
      (this.currentData.analystRating.hold || 0) +
      (this.currentData.analystRating.sell || 0) +
      (this.currentData.analystRating.strong_sell || 0);

    const getWidth = (value: number) => {
      if (!totalAnalysts || totalAnalysts === 0) return '0%';
      return `${(value / totalAnalysts * 100).toFixed(2)}%`;
    };


    const html = `
  <div class="gaugeChartDv">
    <p class="greenBtnBox">${this.currentData.analystRating.consensus}</p>
    <p class="analystBox">Analyst <span>${this.currentData.analystRating.analyst_count}</span></p>
    <div id="gauge-chart" style="width: 100%; height: 150px;"></div>
  </div>

  <div class="statsBar">
    <div class="item">
      <p>Strong Buy</p>
      <div class="bar green01"><span style="width: ${getWidth(this.currentData.analystRating.strong_buy)};"></span></div>
      <p>${this.currentData.analystRating.strong_buy}</p>
    </div>
    <div class="item">
      <p>Buy</p>
      <div class="bar green02"><span style="width: ${getWidth(this.currentData.analystRating.buy)};"></span></div>
      <p>${this.currentData.analystRating.buy}</p>
    </div>
    <div class="item">
      <p>Hold</p>
      <div class="bar yellow"><span style="width: ${getWidth(this.currentData.analystRating.hold)};"></span></div>
      <p>${this.currentData.analystRating.hold}</p>
    </div>
    <div class="item">
      <p>Sell</p>
      <div class="bar orange"><span style="width: ${getWidth(this.currentData.analystRating.sell)};"></span></div>
      <p>${this.currentData.analystRating.sell}</p>
    </div>
    <div class="item">
      <p>Strong Sell</p>
      <div class="bar red"><span style="width: ${getWidth(this.currentData.analystRating.strong_sell)};"></span></div>
      <p>${this.currentData.analystRating.strong_sell}</p>
    </div>
  </div>
`;

    container.innerHTML = html;
    requestAnimationFrame(() => this.drawChart());
  }

  private drawChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#gauge-chart') as HTMLElement;
    if (!echarts || !container) return;
    const instance = echarts.getInstanceByDom(container);
    if (instance) echarts.dispose(instance);

    const consensus = String(this.currentData.analystRating?.consensus ?? '').toLowerCase().replace(/\s+/g, '_');
    let gaugeValue = 65;
    if (consensus === 'strong_buy') gaugeValue = 90;
    else if (consensus === 'buy') gaugeValue = 75;
    else if (consensus === 'hold') gaugeValue = 50;
    else if (consensus === 'sell') gaugeValue = 25;
    else if (consensus === 'strong_sell') gaugeValue = 10;

    const option = {
      series: [
        {
          type: 'gauge',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          splitNumber: 5,
          center: ['50%', '80%'],
          radius: '100%',
          axisLine: {
            lineStyle: {
              width: 5,
              color: [
                [0.2, '#FF3B3B'],  // red
                [0.4, '#FF895A'],  // orange
                [0.6, '#CBFF18'],  // yellow
                [0.8, '#34E36F'],  // light green
                [1, '#009E3A']     // green
              ]
            }
          },
          pointer: {
            icon: 'path://M2,0 L-2,0 L0,-80 Z',
            length: '80%',
            width: 6,
            offsetCenter: [0, '0%'],
            itemStyle: { color: this.config.theme === 'white' ? '#333' : '#fff' }
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          anchor: {
            show: true,
            size: 10,
            itemStyle: { color: '#fff' }
          },
          detail: { show: false },
          data: [{ value: gaugeValue }]
        }
      ]
    };
    this.currentChart = echarts.init(container);
    this.currentChart.setOption(option);
    this.currentChart.resize();
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId) => this.setupChart(stockId), (type, data) => this.emit(type, data));
  }


  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }
  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }
  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }
  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }
public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
  if (!this.shadowRoot) return;

  this.config.theme = toggleWidgetTheme(
    this.shadowRoot,
    theme
  ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

  this.updateDisplay();
}
  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }
  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseStockAssetSpecificUpgradesDowngrades: typeof TraderverseStockAssetSpecificUpgradesDowngrades; } }
if (typeof window !== 'undefined') { window.TraderverseStockAssetSpecificUpgradesDowngrades = TraderverseStockAssetSpecificUpgradesDowngrades; }
